import { useRouter } from "next/router";
import { createContext, useContext, ReactNode, useState, useCallback, useMemo } from "react";

import { DEFAULT_LOCALE } from "@lib/api/clear_fashion/apiConfig";
import { ThemeCode } from "@lib/helper/themeUtils";
import { ICriteriumNode, ISectionNode, ISubcriteriumNode } from "@lib/store/models/nodesModels";
import { ITheme } from "@lib/store/models/themesModel";

export type SectionsGetter = (themeCode: ThemeCode) => Promise<ISectionNode[]>;
export type CriteriumsGetter = (sectionId: string) => Promise<ICriteriumNode[]>;
export type SubcriteriumsGetter = (criteriumId: string) => Promise<ISubcriteriumNode[]>;

export interface ReferentialSectionDisplayOptions {
  withTextScore?: boolean;
  withBonusSection?: boolean;
}

export type ReferentialContextType = {
  themes: ITheme[];
  getSections: SectionsGetter;
  getCriteriums: CriteriumsGetter;
  getSubcriteriums: SubcriteriumsGetter;
  depth?: ReferentialDepth;
  sectionDisplayOptions?: ReferentialSectionDisplayOptions;
};

const DEFAULT_STATE: ReferentialContextType = {
  themes: [],
  getSections: () => Promise.resolve([]),
  getCriteriums: () => Promise.resolve([]),
  getSubcriteriums: () => Promise.resolve([]),
  depth: "subcriteriums",
  sectionDisplayOptions: {
    withTextScore: false,
    withBonusSection: true,
  },
};

export const ReferentialContext = createContext(DEFAULT_STATE);

interface StaticSectionContextProps {
  sectionsByTheme: { [themeCode: string]: ISectionNode[] };
}

interface LazySectionContextProps {
  getSections: SectionsGetter;
}

interface StaticCriteriumContextProps {
  criteriumsBySection: { [sectionId: string]: ICriteriumNode[] };
}

interface LazyCriteriumContextProps {
  getCriteriums: CriteriumsGetter;
}

interface StaticSubcriteriumContextProps {
  subcriteriumsByCriterium: { [criteriumId: string]: ISubcriteriumNode[] };
}

interface LazySubcriteriumContextProps {
  getSubcriteriums: SubcriteriumsGetter;
}

export type ReferentialDepth = "themes" | "sections" | "criteriums" | "subcriteriums";

export type ReferentialProps = {
  themes: ITheme[];
  depth?: ReferentialDepth;
  sectionDisplayOptions?: ReferentialSectionDisplayOptions;
} & ((StaticSectionContextProps & LazySectionContextProps) | LazySectionContextProps) &
  ((StaticCriteriumContextProps & LazyCriteriumContextProps) | LazyCriteriumContextProps) &
  ((StaticSubcriteriumContextProps & LazySubcriteriumContextProps) | LazySubcriteriumContextProps);

export type ReferentialContextProps = ReferentialProps & { children: ReactNode };

const ReferentialProvider = ({
  children,
  themes,
  depth = "subcriteriums",
  sectionDisplayOptions,
  ...props
}: ReferentialContextProps): JSX.Element => {
  const { locale } = useRouter();
  const activeLocale = useMemo(() => locale ?? DEFAULT_LOCALE, [locale]);
  const [error, setError] = useState<Error | null>(null);
  const [sectionsByThemeState, setSectionsByTheme] = useState<{
    [locale: string]: { [themeCode: string]: ISectionNode[] };
  }>({ [activeLocale]: (props as StaticSectionContextProps).sectionsByTheme ?? {} });
  const [criteriumsBySectionState, setCriteriumsBySection] = useState<{
    [locale: string]: { [sectionId: string]: ICriteriumNode[] };
  }>({ [activeLocale]: (props as StaticCriteriumContextProps).criteriumsBySection ?? {} });
  const [subcriteriumsByCriteriumState, setSubcriteriumsByCriterium] = useState<{
    [locale: string]: {
      [sectionId: string]: ISubcriteriumNode[];
    };
  }>({ [activeLocale]: (props as StaticSubcriteriumContextProps).subcriteriumsByCriterium ?? {} });

  if (error) throw error;

  const _getSections = useCallback(
    async (themeCode: string): Promise<ISectionNode[]> => {
      const localizedStoredSections = sectionsByThemeState[activeLocale];
      if (localizedStoredSections) {
        const storedSections = localizedStoredSections[themeCode];
        if (storedSections) return Promise.resolve(storedSections);
      }

      const getter = (props as LazySectionContextProps).getSections;
      if (!getter) return Promise.resolve([]);

      return getter(themeCode as ThemeCode)
        .then((sections) => {
          setSectionsByTheme((previous) => ({
            ...previous,
            [activeLocale]: { ...(previous[activeLocale] ?? {}), [themeCode]: sections },
          }));
          return sections;
        })
        .catch((error) => {
          setError(error as Error);
          return [];
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeLocale],
  );

  const _getCriteriums = useCallback(
    async (sectionId: string): Promise<ICriteriumNode[]> => {
      const localizedStoredCriteriums = criteriumsBySectionState[activeLocale];
      if (localizedStoredCriteriums) {
        const storedCriteriums = localizedStoredCriteriums[sectionId];
        if (storedCriteriums) return Promise.resolve(storedCriteriums);
      }

      const getter = (props as LazyCriteriumContextProps).getCriteriums;
      if (!getter) return Promise.resolve([]);

      return getter(sectionId)
        .then((criteriums) => {
          setCriteriumsBySection((previous) => ({
            ...previous,
            [activeLocale]: { ...(previous[activeLocale] ?? {}), [sectionId]: criteriums },
          }));
          return criteriums;
        })
        .catch((error) => {
          setError(error as Error);
          return [];
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeLocale],
  );

  const _getSubcriteriums = useCallback(
    async (criteriumId: string): Promise<ISubcriteriumNode[]> => {
      const localizedStoredSubcriteriums = subcriteriumsByCriteriumState[activeLocale];
      if (localizedStoredSubcriteriums) {
        const storedSubcriteriums = localizedStoredSubcriteriums[criteriumId];
        if (storedSubcriteriums) return Promise.resolve(storedSubcriteriums);
      }

      const getter = (props as LazySubcriteriumContextProps).getSubcriteriums;
      if (!getter) return Promise.resolve([]);

      return getter(criteriumId)
        .then((subcriteriums) => {
          setSubcriteriumsByCriterium((previous) => ({
            ...previous,
            [activeLocale]: { ...(previous[activeLocale] ?? {}), [criteriumId]: subcriteriums },
          }));
          return subcriteriums;
        })
        .catch((error) => {
          setError(error as Error);
          return [];
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeLocale],
  );

  return (
    <ReferentialContext.Provider
      value={{
        themes,
        getSections: _getSections,
        getCriteriums: _getCriteriums,
        getSubcriteriums: _getSubcriteriums,
        depth,
        sectionDisplayOptions,
      }}
    >
      {children}
    </ReferentialContext.Provider>
  );
};

export const useReferential = () => useContext(ReferentialContext);

export default ReferentialProvider;
